import { Briefcase, Code2, BarChart3, Cog, Type } from 'lucide-react';
import { FormField } from '../form-fields/FormField';
import { CardSelect, CardOption } from '../form-fields/CardSelect';
import { CardMultiSelect } from '../form-fields/CardMultiSelect';
import { TextInput } from '../form-fields/TextInput';
import { UserInfoFormData } from '../hooks/useUserInfoForm';

interface Step4RoleProps {
  formData: UserInfoFormData;
  loading: boolean;
  updateField: (field: keyof UserInfoFormData, value: string | string[]) => void;
  wiggleFields: Set<string>;
}

export function Step4Role({ formData, loading, updateField, wiggleFields }: Step4RoleProps) {
  const rolePresetOptions: CardOption[] = [
    { value: 'Trader', label: 'Trader', icon: BarChart3 },
    { value: 'Analyst', label: 'Analyst', icon: Briefcase },
    { value: 'Developer', label: 'Developer', icon: Code2 },
    { value: 'Ops', label: 'Ops', icon: Cog },
    { value: 'Other', label: 'Other', icon: Type },
  ];

  // Determine which card is selected
  const selectedRole = ['Trader', 'Analyst', 'Developer', 'Ops'].includes(formData.roleDescription)
    ? formData.roleDescription
    : formData.roleDescription === 'Other' ||
        (formData.roleDescription && formData.roleDescription !== '')
      ? 'Other'
      : '';

  const showTextInput = selectedRole === 'Other';

  const useCaseOptions: CardOption[] = [
    { value: 'Production forecasts', label: 'Production forecasts' },
    { value: 'Trading strategies', label: 'Trading strategies' },
    { value: 'Grid-Balancing', label: 'Grid-Balancing' },
    { value: 'R&D', label: 'R&D' },
    { value: 'Analytics', label: 'Analytics' },
    { value: 'Other', label: 'Other' },
  ];

  const goalOptions: CardOption[] = [
    { value: 'Improve forecast precision', label: 'Improve forecast precision' },
    { value: 'Optimize trading decision', label: 'Optimize trading decision' },
    { value: 'Reduce risk', label: 'Reduce risk' },
    { value: 'Reduce business expenses', label: 'Reduce business expenses' },
    { value: 'Other', label: 'Other' },
  ];

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h2 className="text-lg font-medium text-theme-light-text-primary dark:text-theme-dark-text-primary mb-1">
          Tell us about yourself
        </h2>
        <p className="text-sm text-theme-light-text-secondary dark:text-theme-dark-text-secondary">
          Help us personalize your experience
        </p>
      </div>

      <div className="space-y-3">
        {/* Role Section */}
        <div className={wiggleFields.has('roleDescription') ? 'wiggle-animation' : ''}>
          <FormField label="Your Role" htmlFor="roleDescription" required>
            <div className="space-y-2">
              <CardSelect
                options={rolePresetOptions}
                value={selectedRole}
                onChange={(val) => {
                  updateField('roleDescription', val);
                }}
                disabled={loading}
              />
              {showTextInput && (
                <TextInput
                  id="roleDescription"
                  name="roleDescription"
                  placeholder="Type your role"
                  value={formData.roleDescription === 'Other' ? '' : formData.roleDescription}
                  onChange={(e) => updateField('roleDescription', e.target.value)}
                  disabled={loading}
                  required
                />
              )}
            </div>
          </FormField>
        </div>

        {/* Use Case Section */}
        <div className={wiggleFields.has('useCase') ? 'wiggle-animation' : ''}>
          <FormField label="How would you like to use Jua?" htmlFor="useCase" required>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Select all that apply</p>
            <div className="space-y-2">
              <CardMultiSelect
                options={useCaseOptions}
                value={formData.useCase}
                onChange={(val) => updateField('useCase', val)}
                disabled={loading}
              />
              {formData.useCase.includes('Other') && (
                <FormField
                  label="Please specify (optional)"
                  htmlFor="useCaseOther"
                  helpText="You can leave this blank if you prefer not to specify"
                >
                  <TextInput
                    id="useCaseOther"
                    name="useCaseOther"
                    placeholder="Please specify your use case"
                    value={formData.useCaseOther}
                    onChange={(e) => updateField('useCaseOther', e.target.value)}
                    disabled={loading}
                  />
                </FormField>
              )}
            </div>
          </FormField>
        </div>

        {/* Goals Section */}
        <div className={wiggleFields.has('goals') ? 'wiggle-animation' : ''}>
          <FormField label="What is your goal?" htmlFor="goals" required>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Select all that apply</p>
            <div className="space-y-2">
              <CardMultiSelect
                options={goalOptions}
                value={formData.goals}
                onChange={(val) => updateField('goals', val)}
                disabled={loading}
              />
              {formData.goals.includes('Other') && (
                <FormField
                  label="Please specify (optional)"
                  htmlFor="goalsOther"
                  helpText="You can leave this blank if you prefer not to specify"
                >
                  <TextInput
                    id="goalsOther"
                    name="goalsOther"
                    placeholder="Please specify your goal"
                    value={formData.goalsOther}
                    onChange={(e) => updateField('goalsOther', e.target.value)}
                    disabled={loading}
                  />
                </FormField>
              )}
            </div>
          </FormField>
        </div>
      </div>
    </div>
  );
}
